<script setup lang="ts">
import { usePejer } from '@/Pejer'
import { InvoicesEmbed } from '@/Pejer/tables/Invoices'
import { MailCheck } from 'lucide-vue-next'
import DataStateButton from 'pejer/src/components/DataState/DataStateButton.vue'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'pejer/src/components/ui/accordion'
import { Button } from 'pejer/src/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'pejer/src/components/ui/dialog'
import { Separator } from 'pejer/src/components/ui/separator'
import { toast } from 'pejer/src/components/ui/toast'
import usePejerMutation from 'pejer/src/usePejerMutation'
import { ref } from 'vue'
import SignDebtCollectionAgreement from './SignDebtCollectionAgreement.vue'
const props = defineProps<{
  invoice: InvoicesEmbed.ListItem
}>()

const pejer = usePejer()

const { mutate, dataState } = usePejerMutation(pejer.urls.invoices.startDebtCollection, {
  queryKeys: [['invoices'], ['payments']],
  onSuccess: () => {
    toast({
      title: 'Betalingsoppfølging startet',
      description: 'Vi har startet betalingsoppfølging på fakturaen.',
    })
  },
})
const modalOpen = ref(false)
</script>
<template>
  <Dialog v-model:open="modalOpen">
    <DialogTrigger @click.prevent.stop as-child>
      <slot>
        <Button variant="ghost" class="w-full">
          <MailCheck class="w-4 h-4 mr-2" /> Start betalingsoppfølging
        </Button>
      </slot>
    </DialogTrigger>
    <DialogContent class="max-w-2xl overflow-y-scroll max-h-screen">
      <SignDebtCollectionAgreement
        v-if="!invoice.hasApprovedPersonalDebtCollectionAgreement"
        :business-name="props.invoice.connectedAccountSeller.businessName"
        :countryCode="props.invoice.connectedAccountSeller.countryCode"
        :organizationId="props.invoice.connectedAccountSeller.business_TaxID"
        @signed="modalOpen = false"
        @cancelled="modalOpen = false"
      >
        <MailCheck class="w-4 h-4 mr-2" /> Start betalingsoppfølging
      </SignDebtCollectionAgreement>
      <template v-else>
        <DialogHeader>
          <DialogTitle> Start automatisk Betalingsoppfølging </DialogTitle>
          <DialogDescription>
            Ønsker du å starte automatisk betalingsoppfølging på denne fakturaen?
            <br />
            <br />
            Kunde: <b>{{ props.invoice.kidNorwayInvoice.customerName }}</b>
            <br />
            Epost: <b>{{ props.invoice.kidNorwayInvoice.customerEmail }}</b>
            <br />
            Beløp: <b>{{ props.invoice.grossAmount }} kr</b>
            <br />
            Forfallsdato:
            <b>{{ new Date(props.invoice.kidNorwayInvoice.dueDate).toLocaleDateString() }}</b>
            <br />
            Fakturanummer: <b>{{ props.invoice.paymentID }}</b>

            <Separator class="my-4" />
            <h2 class="text-base font-semibold mb-2">Hva innebærer dette?</h2>
            <Accordion type="single" class="w-full" collapsible>
              <AccordionItem value="item.value">
                <AccordionTrigger> Hva er prosessen for oppfølging? </AccordionTrigger>
                <AccordionContent>
                  <p>
                    Når du starter betalingsoppfølging, vil Bucky ta hånd om hele prosessen på vegne
                    av
                    <b>{{ invoice.connectedAccountSeller.businessName }}</b
                    >. Vi sender først en purring tidligst 14 dager etter forfall. Dersom kunden
                    fortsatt ikke betaler, sender vi et inkassovarsel, og til slutt en
                    betalingsoppfordring i tråd med inkassoloven.
                  </p>
                  <br />
                  <p>
                    Oppfølgingen er <b>kostnadsfri</b> for deg som oppdragsgiver. Du mottar alltid
                    hele hovedbeløpet dersom kunden betaler. Eventuelle gebyrer og renter legges på
                    fakturaen og tilfaller Bucky som betaling for oppfølgingstjenesten.
                  </p>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item.value2">
                <AccordionTrigger> Satser (som kunden betaler) </AccordionTrigger>
                <AccordionContent>
                  <p>
                    - <b>Første purring:</b> <b>35 kr</b> <i> (tidligst 14 dager etter forfall)</i>
                  </p>
                  <p>
                    - <b>Andre purring:</b> <b>35 kr</b>
                    <i> (tidligst 14 dager etter første purring)</i>
                  </p>
                  <p>
                    - <b>Inkassogebyr:</b> <b>105 kr</b>
                    <i> (tidligst 14 dager etter andre purring) </i>
                  </p>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter class="items-center">
          <DialogClose as-child>
            <Button type="button" variant="secondary"> Avbryt </Button>
          </DialogClose>
          <DataStateButton
            :dataState="dataState"
            @click.prevent.stop="
              () => {
                mutate({
                  pathArgs: {
                    id: props.invoice.paymentReferenceID,
                  },
                  body: {
                    enabled: true,
                  },
                })
                modalOpen = false
              }
            "
          >
            <MailCheck class="w-4 h-4 mr-2" /> Start betalingsoppfølging
          </DataStateButton>
        </DialogFooter>
      </template>
    </DialogContent>
  </Dialog>
</template>
